// src/pages/Dashboard/Admin/AdherentPret/utils/adherentPretDashboardUtils.js

import { toAbsoluteArticlePhotoUrl } from "../../../../../services/articlesPhotoServices";

export const DEFAULT_ARTICLE_PHOTO = "/img/no-image.jpg";

export function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

export function normalizeText(value = "") {
  return String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function normalizeCategory(value = "") {
  return normalizeText(value).replace(/[^a-z0-9]/g, "");
}

export function isPretArticle(article) {
  return normalizeCategory(article?.categorie) === "pret";
}

export function sortAdherents(rows = []) {
  return [...rows].sort((a, b) =>
    `${a?.nom || ""} ${a?.prenom || ""}`.localeCompare(
      `${b?.nom || ""} ${b?.prenom || ""}`,
      "fr",
      { sensitivity: "base" }
    )
  );
}

export function sortArticles(rows = []) {
  return [...rows].sort((a, b) =>
    String(a?.nom || "").localeCompare(String(b?.nom || ""), "fr", {
      sensitivity: "base",
    })
  );
}

export function parseDate(value) {
  if (!value) return null;

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return null;

  return date;
}

export function isValidDate(value) {
  return !!parseDate(value);
}

export function toDateInputValue(value) {
  const date = parseDate(value);
  if (!date) return "";

  return date.toISOString().slice(0, 10);
}

export function formatDateShort(value) {
  const date = parseDate(value);
  if (!date) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

export function formatDateLong(value) {
  const date = parseDate(value);
  if (!date) return "—";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export function getDaysSince(value) {
  const date = parseDate(value);
  if (!date) return 0;

  const today = new Date();
  const diff = today.getTime() - date.getTime();

  return Math.max(0, Math.floor(diff / 86400000));
}

export function getArticlePhotoSrc(photoUrl) {
  if (!photoUrl) return DEFAULT_ARTICLE_PHOTO;

  const absolute = toAbsoluteArticlePhotoUrl(photoUrl);

  return absolute || DEFAULT_ARTICLE_PHOTO;
}

export function getPretAdherentName(pret) {
  return `${pret?.adherent?.nom || ""} ${pret?.adherent?.prenom || ""}`.trim();
}

export function getPretArticleName(pret) {
  return pret?.article?.nom || "—";
}

export function getPretStatus(pret) {
  if (isValidDate(pret?.date_remise)) return "returned";

  const days = getDaysSince(pret?.date_pret);

  if (days >= 30) return "late";

  return "ongoing";
}

export function getPretStatusLabel(status) {
  const labels = {
    ongoing: "En cours",
    returned: "Rendu",
    late: "À relancer",
  };

  return labels[status] || "Non renseigné";
}

export function getPretStatusClass(status) {
  const classes = {
    ongoing:
      "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20",
    returned:
      "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20",
    late:
      "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20",
  };

  return (
    classes[status] ||
    "bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700"
  );
}

export function sortPrets(rows = [], sort = "date_desc") {
  const list = [...rows];

  return list.sort((a, b) => {
    if (sort === "date_asc") {
      return (
        (parseDate(a?.date_pret)?.getTime() || 0) -
        (parseDate(b?.date_pret)?.getTime() || 0)
      );
    }

    if (sort === "name_asc") {
      return getPretAdherentName(a).localeCompare(getPretAdherentName(b), "fr");
    }

    if (sort === "article_asc") {
      return getPretArticleName(a).localeCompare(getPretArticleName(b), "fr");
    }

    if (sort === "status_asc") {
      return getPretStatusLabel(getPretStatus(a)).localeCompare(
        getPretStatusLabel(getPretStatus(b)),
        "fr"
      );
    }

    return (
      (parseDate(b?.date_pret)?.getTime() || 0) -
      (parseDate(a?.date_pret)?.getTime() || 0)
    );
  });
}

export function filterPrets(rows = [], filters = {}) {
  const query = normalizeText(filters.query);
  const status = String(filters.status || "");
  const sort = String(filters.sort || "date_desc");

  const filtered = rows.filter((pret) => {
    const searchable = normalizeText(
      `${getPretAdherentName(pret)} ${getPretArticleName(pret)} ${
        pret?.note_pret || ""
      }`
    );

    const pretStatus = getPretStatus(pret);

    const matchQuery = !query || searchable.includes(query);
    const matchStatus = !status || pretStatus === status;

    return matchQuery && matchStatus;
  });

  return sortPrets(filtered, sort);
}

export function calculatePretStats(allRows = [], visibleRows = []) {
  const total = allRows.length;
  const visible = visibleRows.length;

  const ongoing = visibleRows.filter(
    (pret) => getPretStatus(pret) === "ongoing"
  ).length;

  const returned = visibleRows.filter(
    (pret) => getPretStatus(pret) === "returned"
  ).length;

  const late = visibleRows.filter((pret) => getPretStatus(pret) === "late")
    .length;

  const articles = new Set(
    visibleRows.map((pret) => pret?.article?.id).filter(Boolean)
  );

  return {
    total,
    visible,
    ongoing,
    returned,
    late,
    articleCount: articles.size,
  };
}

export function buildCreatePretPayload(formData = {}) {
  return {
    adherent_id: Number(formData.adherent_id),
    article_id: Number(formData.article_id),
    date_pret: formData.date_pret || todayStr(),
    note_pret: String(formData.note_pret || "").trim() || null,
  };
}

export function buildUpdatePretPayload(formData = {}) {
  return {
    date_remise: isValidDate(formData.date_remise)
      ? formData.date_remise
      : null,
    note_pret: String(formData.note_pret || "").trim() || null,
  };
}